import { Controller, useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";

import { useClientApi, useContentResource } from "@ctv/core";
import {
  Button,
  Card,
  Text,
  Token,
  useLocalizedRouterServer,
  View,
} from "@ctv/shared-core/src";
import { useDispatchSnackbar } from "@ctv/shared/contexts/SnackbarContext";

import IcPaymentCreditCard from "@traveloka/icon-kit-web/react/IcPaymentCreditCard";
import IcPolicy from "@traveloka/icon-kit-web/react/IcPolicy";
import IcSystemArrowLeft from "@traveloka/icon-kit-web/react/IcSystemArrowLeft";

import CreditCardDocumentForm from "./_components/CreditCardDocumentForm";
import InvoiceForm from "./_components/InvoiceForm";
import RadioPaymentMethod from "./_components/RadioPaymentMethod";
import { handleProcessDataBeforeSubmit } from "./helper";

import { useRegisterContent } from "../../_contexts/RegisterContentContext";
import {
  SAVE_AND_EXIT_API,
  SaveAndExitRequest,
  SaveAndExitResponse,
} from "../../apis";
import { PaymentMethod, RegistrationFormValue } from "../../types";

type Props = {
  onSubmit: (value: RegistrationFormValue) => void;
  onClickBack?: () => void;
};

export default function RegistrationPaymentMethod({
  onSubmit,
  onClickBack,
}: Props) {
  const router = useLocalizedRouterServer();
  const openSnackbar = useDispatchSnackbar();
  const { token } = useRegisterContent();
  const { CorporateRegistration: cr } = useContentResource();
  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = useFormContext<RegistrationFormValue>();
  const [paymentMethod] = getValues(["paymentMethod"]);

  const save = useClientApi<SaveAndExitResponse, SaveAndExitRequest>({
    domain: "management",
    method: "post",
    path: SAVE_AND_EXIT_API,
  });

  const PAYMENT_OPTIONS = [
    {
      type: PaymentMethod.CREDIT_CARD,
      title: cr.creditCard,
      description: cr.creditCardHelpV2,
      benefits: cr.creditCardBenefitsV3,
      icon: <IcPaymentCreditCard height={24} width={24} />,
      form: <CreditCardDocumentForm />,
    },
    {
      type: PaymentMethod.INVOICE,
      title: cr.invoice,
      description: cr.invoiceHelpV2,
      benefits: cr.invoiceBenefitsV3,
      icon: <IcPolicy height={24} width={24} />,
      form: <InvoiceForm />,
    },
  ];

  function handleOnClickSaveAndExit() {
    const transformData = handleProcessDataBeforeSubmit(getValues());
    const payload: SaveAndExitRequest = {
      ...transformData,
      token,
    };
    save(payload)
      .then((res) => {
        if (res.success) {
          router.push("/");
          window.scrollTo({ top: 0 });
        } else {
          throw new Error("Failed to save and exit");
        }
      })
      .catch(() => {
        openSnackbar({ message: cr.submitErrorMessage, variant: "alert" });
      });
  }

  return (
    <View style={styles.container} row align="start">
      <View style={styles.startContent}>
        <Button
          text={cr.back}
          variant="text-primary"
          style={styles.alignSelfStart}
          startIcon={<IcSystemArrowLeft height={16} width={16} />}
          onPress={onClickBack}
        />
        <Text variant="title-1" style={styles.section}>
          {cr.paymentMethodSectionV3}
        </Text>
        <Text variant="ui-small" ink="black-secondary" style={styles.help}>
          {cr.paymentMethodHelpV2}
        </Text>
        {/* Radio Group */}
        <Controller
          control={control}
          name="paymentMethod"
          render={({ field: { onChange, value } }) => (
            <View spacing="l">
              {PAYMENT_OPTIONS.map((option, index) => (
                <RadioPaymentMethod
                  key={index}
                  title={option.title}
                  description={option.description}
                  benefits={option.benefits}
                  icon={option.icon}
                  value={option.type}
                  onChange={onChange}
                  checked={value === option.type}
                >
                  {option.form}
                </RadioPaymentMethod>
              ))}
            </View>
          )}
        />
        {/* Action Button */}
        <View style={styles.action} row justify="end">
          <Button
            variant="text-primary"
            text={cr.saveAndExitButtonText}
            onPress={handleOnClickSaveAndExit}
          />
          <Button
            onPress={handleSubmit(onSubmit)}
            loading={isSubmitting}
            style={styles.continue}
            variant="main-cta"
            text={cr.continueButtonText}
            disabled={!paymentMethod}
          />
        </View>
      </View>
      <Card style={styles.endContent}>
        <Text style={styles.endContentTitle} variant="ui-small">
          {cr.moreTimeInfo}
        </Text>
        <Text variant="ui-tiny" ink="black-secondary">
          {cr.moreTimeInfoBody}
        </Text>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 960,
    position: "relative",
    marginTop: Token.spacing.xxl,
    marginHorizontal: "auto",
  },
  startContent: {
    flex: 1,
    marginRight: Token.spacing.xxxl,
  },
  cardOption: {
    padding: Token.spacing.l,
  },
  radioOption: {
    gap: Token.spacing.xs,
  },
  radioContent: {
    flex: 1,
  },
  section: {
    marginTop: Token.spacing.m,
    marginBottom: Token.spacing.ml,
  },
  help: {
    marginBottom: Token.spacing.l,
  },
  action: {
    marginVertical: Token.spacing.xl,
  },
  continue: {
    marginLeft: Token.spacing.l,
  },
  endContent: {
    width: 266,
    padding: Token.spacing.m,
    // @ts-expect-error
    position: "sticky",
    top: 2 * Token.spacing.xl + Token.spacing.xxxl,
  },
  endContentTitle: {
    fontWeight: "600",
    marginBottom: Token.spacing.m,
  },

  // General styles
  marginTopS: {
    marginTop: Token.spacing.s,
  },
  alignSelfStart: {
    alignSelf: "flex-start",
  },
});
